// src/contexts/AppDataContext.js - Optimized with reduced redundant API calls
import React, { createContext, useContext, useReducer, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import axios from 'axios';
import apiConfig from '../config/api.config';
import { isExcludedInternalClient } from '../utils/clientNameMappings';

// Create context
const AppDataContext = createContext();

// Define initial state
const initialState = {
  summary: {
    total_hosts: 0,
    active_services: 0,
    total_clients: 0,
    total_log_size: 0,
  },
  regions: [],
  hosts: [],
  clients: [],
  systemMetrics: {}, // Initialize as empty object instead of null
  clientLogs: null,
  clientCount: {
    regions: {}
  },
  hostClients: null,
  clientDetails: {},
  clientCosts: {},
  clientLicenses: {},
  alerts: [],
  jiraConfig: {
    enabled: false,
    url: null,
    project_key: null,
    regions: [],
    region_assignees: {}
  },
  alertTickets: [],
  
  // OCI Monitoring specific state
  storageDetails: null,
  storageTrends: null,
  storageCosts: null,
  compartmentTree: null,
  compartmentResources: null,
  compartmentCosts: null,
  shapeAnalysis: null,
  shapeOptimizations: null,
  
  // User information
  userInfo: {
    username: '',
    email: '',
    roleId: '',
    roleDescription: '',
    tenantName: '',
    tenantCode: '',
    isExternal: false,
    sessionId: ''
  },
  
  // Filtering parameters to track current state
  filterParams: {
    region: '',
    timeRange: '24h',
    from_date: '',
    to_date: ''
  },
  
  loading: {
    summary: false,
    alerts: false,
    regions: false,
    hosts: false,
    clients: false,
    systemMetrics: false,
    clientLogs: false,
    clientCount: false,
    hostClients: false,
    clientDetails: false,
    clientCosts: false,
    clientLicenses: false,
    jiraConfig: false,
    alertTickets: false,
    userInfo: false,
    
    // OCI Monitoring loading states
    storageDetails: false,
    storageTrends: false,
    storageCosts: false,
    compartmentTree: false,
    compartmentResources: false,
    compartmentCosts: false,
    shapeAnalysis: false,
    shapeOptimizations: false,
    deviceIds: false
  },
  error: null,
  totalProductionCosts: {
    rsyslog_size: 0,
    production_cost: 0,
    cost_per_gb: 0,
    region: '',
    cost_details: {
      compute_cost: 0,
      boot_volume_cost: 0,
      block_volume_cost: 0
    }
  },
  clientLogsTimeseries: null,
};

// Define actions
export const ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_ALERTS: 'SET_ALERTS',
  CLEAR_ERROR: 'CLEAR_ERROR',
  SET_REGIONS: 'SET_REGIONS',
  SET_HOSTS: 'SET_HOSTS',
  SET_CLIENTS: 'SET_CLIENTS',
  SET_SUMMARY: 'SET_SUMMARY',
  SET_SYSTEM_METRICS: 'SET_SYSTEM_METRICS',
  SET_CLIENT_LOGS: 'SET_CLIENT_LOGS',
  SET_CLIENT_COUNT: 'SET_CLIENT_COUNT',
  SET_HOST_CLIENTS: 'SET_HOST_CLIENTS',
  SET_CLIENT_DETAILS: 'SET_CLIENT_DETAILS',
  SET_JIRA_CONFIG: 'SET_JIRA_CONFIG',
  SET_ALERT_TICKETS: 'SET_ALERT_TICKETS',
  SET_FILTER_PARAMS: 'SET_FILTER_PARAMS',
  SET_CLIENT_LICENSES: 'SET_CLIENT_LICENSES',
  SET_TOTAL_PRODUCTION_COSTS: 'SET_TOTAL_PRODUCTION_COSTS',
  SET_CLIENT_LOGS_TIMESERIES: 'SET_CLIENT_LOGS_TIMESERIES',
  
  // OCI Monitoring specific actions
  SET_STORAGE_DETAILS: 'SET_STORAGE_DETAILS',
  SET_STORAGE_TRENDS: 'SET_STORAGE_TRENDS',
  SET_STORAGE_COSTS: 'SET_STORAGE_COSTS',
  SET_COMPARTMENT_TREE: 'SET_COMPARTMENT_TREE',
  SET_COMPARTMENT_RESOURCES: 'SET_COMPARTMENT_RESOURCES',
  SET_COMPARTMENT_COSTS: 'SET_COMPARTMENT_COSTS',
  SET_SHAPE_ANALYSIS: 'SET_SHAPE_ANALYSIS',
  SET_SHAPE_OPTIMIZATIONS: 'SET_SHAPE_OPTIMIZATIONS',
  SET_USER_INFO: 'SET_USER_INFO',
};

// Define reducer
const reducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: {
          ...state.loading,
          [action.payload.key]: action.payload.value,
        },
      };
    case ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
      };
    case ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };
    case ACTIONS.SET_REGIONS:
      return {
        ...state,
        regions: action.payload,
      };
    case ACTIONS.SET_HOSTS:
      return {
        ...state,
        hosts: action.payload,
      };
    case ACTIONS.SET_CLIENTS:
      return {
        ...state,
        clients: action.payload,
      };
    case ACTIONS.SET_SUMMARY:
      return {
        ...state,
        summary: action.payload,
      };
    case ACTIONS.SET_SYSTEM_METRICS:
      return {
        ...state,
        systemMetrics: action.payload,
      };
    case ACTIONS.SET_CLIENT_LOGS:
      return {
        ...state,
        clientLogs: action.payload,
      };
    case ACTIONS.SET_CLIENT_COUNT:
      return {
        ...state,
        clientCount: action.payload,
      };
    case ACTIONS.SET_HOST_CLIENTS:
      return {
        ...state,
        hostClients: action.payload,
      };
    case ACTIONS.SET_CLIENT_LICENSES:
      return {
        ...state,
        clientLicenses: action.payload,
      };
    case ACTIONS.SET_CLIENT_DETAILS:
      return {
        ...state,
        clientDetails: {
          ...state.clientDetails,
          ...action.payload
        },
      };
    case ACTIONS.SET_ALERTS:
      return {
        ...state,
        alerts: action.payload,
      };
    case ACTIONS.SET_JIRA_CONFIG:
      return {
        ...state,
        jiraConfig: {
          ...action.payload,
          // Ensure jira_enabled property is maintained for consistency with backend
          jira_enabled: action.payload.jira_enabled !== undefined ? action.payload.jira_enabled : action.payload.enabled
        },
      };
    case ACTIONS.SET_ALERT_TICKETS:
      return {
        ...state,
        alertTickets: action.payload,
      };
    case ACTIONS.SET_FILTER_PARAMS:
      return {
        ...state,
        filterParams: {
          ...state.filterParams,
          ...action.payload
        },
      };
      
    // OCI Monitoring specific reducers
    case ACTIONS.SET_STORAGE_DETAILS:
      return {
        ...state,
        storageDetails: action.payload,
      };
    case ACTIONS.SET_STORAGE_TRENDS:
      return {
        ...state,
        storageTrends: action.payload,
      };
    case ACTIONS.SET_STORAGE_COSTS:
      return {
        ...state,
        storageCosts: action.payload,
      };
    case ACTIONS.SET_COMPARTMENT_TREE:
      return {
        ...state,
        compartmentTree: action.payload,
      };
    case ACTIONS.SET_COMPARTMENT_RESOURCES:
      return {
        ...state,
        compartmentResources: action.payload,
      };
    case ACTIONS.SET_COMPARTMENT_COSTS:
      return {
        ...state,
        compartmentCosts: action.payload,
      };
    case ACTIONS.SET_SHAPE_ANALYSIS:
      return {
        ...state,
        shapeAnalysis: action.payload,
      };
    case ACTIONS.SET_SHAPE_OPTIMIZATIONS:
      return {
        ...state,
        shapeOptimizations: action.payload,
      };
    case ACTIONS.SET_USER_INFO:
      return {
        ...state,
        userInfo: action.payload,
      };
    case ACTIONS.SET_TOTAL_PRODUCTION_COSTS:
      return {
        ...state,
        totalProductionCosts: action.payload,
      };
    case ACTIONS.SET_CLIENT_LOGS_TIMESERIES:
      return {
        ...state,
        clientLogsTimeseries: action.payload,
      };
    default:
      return state;
  }
};

// Create mock data
const createMockData = () => {
  // Mock regions
  const regions = ['ap-mumbai-1', 'me-abudhabi-1', 'ap-hyderabad-1', 'eu-frankfurt-1', 'us-ashburn-1'];
  
  // Mock hosts
  const hosts = [
    'RUH-SERVER-01',
    'RUH-SERVER-02',
    'JED-SERVER-01', 
    'DMM-SERVER-01',
    'AUH-SERVER-01',
  ];
  
  // Mock clients
  const clients = [];
  for (let i = 1; i <= 10; i++) {
    clients.push(`Client${i}`);
  }
  
  // Mock system metrics
  const systemMetrics = {
    cpu: [],
    memory: [],
  };
  
  const now = new Date();
  for (let i = 0; i < 24; i++) {
    const time = new Date(now);
    time.setHours(time.getHours() - i);
    
    hosts.slice(0, 3).forEach(hostname => {
      systemMetrics.cpu.push({
        hostname,
        time: time.toISOString(),
        value: Math.floor(Math.random() * 50) + 30
      });
      
      systemMetrics.memory.push({
        hostname,
        time: time.toISOString(),
        value: Math.floor(Math.random() * 40) + 40
      });
    });
  }

  // Mock host-specific metrics
  const hostMetrics = {};
  hosts.forEach(hostname => {
    // Create consistent random values based on hostname
    const hash = hostname.split('').reduce((sum, char) => sum + char.charCodeAt(0), 0);
    const pseudoRandom = (seed) => (hash * seed % 101) / 100;
    
    hostMetrics[hostname] = {
      cpu: 30 + pseudoRandom(7919) * 60,
      memory: 40 + pseudoRandom(7907) * 50,
      disk: 50 + pseudoRandom(7901) * 40,
      status: pseudoRandom(7883) > 0.2 ? 'active' : 'inactive',
      region: hostname.split('-')[0],
    };
  });
  
  // Mock client logs
  const clientLogs = {
    by_client: [],
    by_product: [],
    timeseries: []
  };
  
  clients.forEach(client_name => {
    const size_gb = Math.floor(Math.random() * 25) + 5;
    clientLogs.by_client.push({ client_name, size_gb });
    
    ['Product A', 'Product B', 'Product C'].forEach(product => {
      const product_size = size_gb / 3 * (0.7 + Math.random() * 0.6);
      clientLogs.by_product.push({
        client_name,
        product,
        size_gb: product_size
      });
    });
    
    for (let i = 0; i < 7; i++) {
      const time = new Date(now);
      time.setDate(time.getDate() - i);
      
      clientLogs.timeseries.push({
        client_name,
        time: time.toISOString(),
        size_gb: Math.floor(Math.random() * 15) + 5
      });
    }
  });
  
  // Mock summary
  const summary = {
    total_hosts: hosts.length,
    active_services: hosts.length - 1,
    total_clients: clients.length,
    total_log_size: 125.5,
  };
  
  // Mock client count
  const clientCount = {
    regions: {}
  };
  
  regions.forEach((region, index) => {
    clientCount.regions[region] = {
      client_count: 10 - index
    };
  });
  
  // Mock storage details
  const storageDetails = {
    boot_volumes: {
      total_size: 2450,
      total_cost: 125.75,
      volume_count: 113
    },
    block_volumes: {
      total_size: 8320,
      total_cost: 298.56,
      volume_count: 113
    },
    by_region: [
      {
        region: "ap-mumbai-1",
        boot_volume_size: 1380,
        block_volume_size: 5200
      },
      {
        region: "me-abudhabi-1",
        boot_volume_size: 1070,
        block_volume_size: 3120
      }
    ],
    top_consumers: Array(10).fill(0).map((_, i) => ({
      instance_name: `BOM-NEW-OS-DATAWARM${i.toString().padStart(2, '0')}`,
      region_name: i % 2 === 0 ? "ap-mumbai-1" : "me-abudhabi-1",
      compartment_name: i % 2 === 0 ? "eventussecurity-bom" : "eventussecurity-AUH",
      boot_volume_size: 100 + (i * 20),
      boot_volume_price: 5.2 + (i * 1.1),
      block_volume_total_size: 200 + (i * 100),
      block_volume_total_cost: 10.5 + (i * 5.7),
      storage_cost: 15.7 + (i * 6.8)
    }))
  };
  
  // Mock storage trends
  const storageTrends = {
    data: Array(30).fill(0).map((_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - 30 + i);
      const dateStr = date.toISOString().split('T')[0];
      
      // Create an increasing trend
      const baseBoot = 2000 + (i * 20);
      const baseBlock = 6000 + (i * 100);
      
      return {
        date: dateStr,
        boot_volume_size: baseBoot + Math.random() * 200,
        block_volume_size: baseBlock + Math.random() * 400,
        total_size: baseBoot + baseBlock + Math.random() * 600
      };
    })
  };
  
  // Mock storage costs
  const storageCosts = {
    data: Array(30).fill(0).map((_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - 30 + i);
      const dateStr = date.toISOString().split('T')[0];
      
      // Create an increasing trend
      const baseBoot = 4 + (i * 0.1);
      const baseBlock = 8 + (i * 0.3);
      
      return {
        date: dateStr,
        boot_volume_cost: baseBoot + Math.random() * 1,
        block_volume_cost: baseBlock + Math.random() * 2,
        total_cost: baseBoot + baseBlock + Math.random() * 3
      };
    })
  };
  
  // Mock compartment tree
  const compartmentTree = [
    {
      region_name: "ap-mumbai-1",
      compartments: [
        {
          compartment_name: "eventussecurity-bom",
          compartment_id: "ocid1.compartment.oc1..aaaaaaanwnanap1j7r5yh7q4v5mthxt74ahv7vjsse",
          instance_count: 45,
          total_cost: 2950.75
        },
        {
          compartment_name: "Mumbai_Test",
          compartment_id: "ocid1.compartment.oc1..aaaaaaasc86tz2f59wdnrys8cjbw46",
          instance_count: 12,
          total_cost: 850.25
        }
      ]
    },
    {
      region_name: "me-abudhabi-1",
      compartments: [
        {
          compartment_name: "eventussecurity-AUH",
          compartment_id: "ocid1.compartment.oc1..aaaaaaanwnanap1j7r5yh7q4v5mthxt74ahv7vjsse",
          instance_count: 28,
          total_cost: 1450.80
        }
      ]
    }
  ];
  
  // Mock compartment resources
  const compartmentResources = [
    {
      region_name: "ap-mumbai-1",
      compartment_name: "eventussecurity-bom",
      compartment_id: "ocid1.compartment.oc1..aaaaaaanwnanap1j7r5yh7q4v5mthxt74ahv7vjsse",
      instance_count: 45,
      total_cpu: 128,
      total_memory: 512,
      total_boot_volume: 4500,
      total_block_volume: 12000
    },
    {
      region_name: "ap-mumbai-1",
      compartment_name: "Mumbai_Test",
      compartment_id: "ocid1.compartment.oc1..aaaaaaasc86tz2f59wdnrys8cjbw46",
      instance_count: 12,
      total_cpu: 36,
      total_memory: 144,
      total_boot_volume: 1200,
      total_block_volume: 3600
    },
    {
      region_name: "me-abudhabi-1",
      compartment_name: "eventussecurity-AUH",
      compartment_id: "ocid1.compartment.oc1..aaaaaaanwnanap1j7r5yh7q4v5mthxt74ahv7vjsse",
      instance_count: 28,
      total_cpu: 84,
      total_memory: 336,
      total_boot_volume: 2800,
      total_block_volume: 8400
    }
  ];
  
  // Mock compartment costs
  const compartmentCosts = [
    {
      region_name: "ap-mumbai-1",
      compartment_name: "eventussecurity-bom",
      compartment_id: "ocid1.compartment.oc1..aaaaaaanwnanap1j7r5yh7q4v5mthxt74ahv7vjsse",
      compute_cost: 1800.50,
      block_storage_cost: 800.25,
      file_storage_cost: 150.00,
      mysql_cost: 0,
      load_balancer_cost: 100.00,
      virtual_cloud_network_cost: 50.00,
      total_cost: 2950.75
    },
    {
      region_name: "ap-mumbai-1",
      compartment_name: "Mumbai_Test",
      compartment_id: "ocid1.compartment.oc1..aaaaaaasc86tz2f59wdnrys8cjbw46",
      compute_cost: 600.20,
      block_storage_cost: 200.05,
      file_storage_cost: 0,
      mysql_cost: 0,
      load_balancer_cost: 50.00,
      virtual_cloud_network_cost: 0,
      total_cost: 850.25
    },
    {
      region_name: "me-abudhabi-1",
      compartment_name: "eventussecurity-AUH",
      compartment_id: "ocid1.compartment.oc1..aaaaaaanwnanap1j7r5yh7q4v5mthxt74ahv7vjsse",
      compute_cost: 1000.30,
      block_storage_cost: 300.50,
      file_storage_cost: 50.00,
      mysql_cost: 0,
      load_balancer_cost: 100.00,
      virtual_cloud_network_cost: 0,
      total_cost: 1450.80
    }
  ];
  
  // Mock shape analysis
  const shapes = [
    {
      vm_shape: "VM.Standard.A1.Flex",
      instance_count: 32,
      total_cpu: 96,
      total_memory: 384,
      daily_cost: 225.60,
      cpu_per_instance: 3,
      memory_per_instance: 12,
      avg_cpu_usage_percent: 42.5,
      avg_memory_usage_percent: 38.7
    },
    {
      vm_shape: "VM.Standard.E4.Flex",
      instance_count: 25,
      total_cpu: 100,
      total_memory: 400,
      daily_cost: 300.00,
      cpu_per_instance: 4,
      memory_per_instance: 16,
      avg_cpu_usage_percent: 35.2,
      avg_memory_usage_percent: 45.3
    },
    {
      vm_shape: "VM.Standard.E3.Flex",
      instance_count: 18,
      total_cpu: 72,
      total_memory: 216,
      daily_cost: 180.00,
      cpu_per_instance: 4,
      memory_per_instance: 12,
      avg_cpu_usage_percent: 65.8,
      avg_memory_usage_percent: 72.4
    },
  ];
  
  // Generate instance data
  const mockInstances = Array(100).fill(0).map((_, i) => {
    const shapeNames = ["VM.Standard.A1.Flex", "VM.Standard.E4.Flex", "VM.Standard.E3.Flex"];
    const regions = ["ap-mumbai-1", "me-abudhabi-1"];
    const compartments = ["eventussecurity-bom", "eventussecurity-AUH", "Mumbai_Test"];
    
    const shapeIndex = i % shapeNames.length;
    const regionIndex = i % regions.length;
    const compartmentIndex = i % compartments.length;
    
    return {
      instance_name: `Instance-${i}`,
      region_name: regions[regionIndex],
      compartment_name: compartments[compartmentIndex],
      instance_state: "RUNNING",
      vm_shape: shapeNames[shapeIndex],
      cpu: shapeIndex === 0 ? 3 : 4,
      memory: shapeIndex === 0 ? 12 : (shapeIndex === 1 ? 16 : 12),
      compute_cost: shapeIndex === 0 ? 7.05 : (shapeIndex === 1 ? 12.00 : 10.00)
    };
  });
  
  const shapeAnalysis = {
    shapes: shapes,
    instances: mockInstances,
    summary: {
      shape_count: shapes.length,
      instance_count: shapes.reduce((sum, shape) => sum + shape.instance_count, 0),
      total_cpu: shapes.reduce((sum, shape) => sum + shape.total_cpu, 0),
      total_memory: shapes.reduce((sum, shape) => sum + shape.total_memory, 0),
      total_cost: shapes.reduce((sum, shape) => sum + shape.daily_cost, 0)
    }
  };
  
  // Mock shape optimizations
  const recommendations = Array(15).fill(0).map((_, i) => {
    const shapeNames = ["VM.Standard.A1.Flex", "VM.Standard.E4.Flex", "VM.Standard.E3.Flex"];
    const regions = ["ap-mumbai-1", "me-abudhabi-1"];
    const compartments = ["eventussecurity-bom", "eventussecurity-AUH", "Mumbai_Test"];
    
    const shapeIndex = i % shapeNames.length;
    const regionIndex = i % regions.length;
    const compartmentIndex = i % compartments.length;
    
    const currentCPU = shapeIndex === 0 ? 3 : 4;
    const currentMemory = shapeIndex === 0 ? 12 : (shapeIndex === 1 ? 16 : 12);
    const currentCost = shapeIndex === 0 ? 7.05 : (shapeIndex === 1 ? 12.00 : 10.00);
    
    const recommendedCPU = Math.max(1, Math.round(currentCPU * 0.5));
    const recommendedMemory = Math.max(1, Math.round(currentMemory * 0.6));
    const recommendedCost = currentCost * (recommendedCPU / currentCPU) * 0.9;
    
    return {
      instance_name: `Instance-${i}`,
      region_name: regions[regionIndex],
      compartment_name: compartments[compartmentIndex],
      current_shape: shapeNames[shapeIndex],
      current_cpu: currentCPU,
      current_memory: currentMemory,
      cpu_usage_percent: 20 + Math.random() * 20,
      memory_usage_percent: 20 + Math.random() * 25,
      recommended_shape: `VM.Standard.E4.Flex (OCPUs: ${recommendedCPU}, Memory: ${recommendedMemory} GB)`,
      recommended_cpu: recommendedCPU,
      recommended_memory: recommendedMemory,
      current_cost: currentCost,
      recommended_cost: Math.round(recommendedCost * 100) / 100,
      savings_percent: Math.round((currentCost - recommendedCost) / currentCost * 100 * 10) / 10
    };
  });
  
  // Sort by potential savings
  recommendations.sort((a, b) => (b.current_cost - b.recommended_cost) - (a.current_cost - a.recommended_cost));
  
  const shapeOptimizations = {
    recommendations,
    summary: {
      total_instances: 75,
      optimizable_instances: recommendations.length,
      total_current_cost: recommendations.reduce((sum, rec) => sum + rec.current_cost, 0),
      total_recommended_cost: recommendations.reduce((sum, rec) => sum + rec.recommended_cost, 0),
      total_savings: recommendations.reduce((sum, rec) => sum + (rec.current_cost - rec.recommended_cost), 0),
      savings_percent: Math.round(
        (recommendations.reduce((sum, rec) => sum + (rec.current_cost - rec.recommended_cost), 0) / 
         recommendations.reduce((sum, rec) => sum + rec.current_cost, 0) * 100) * 10
      ) / 10
    }
  };
  
  return {
    regions,
    hosts,
    clients,
    systemMetrics,
    hostMetrics,
    clientLogs,
    summary,
    clientCount,
    storageDetails,
    storageTrends,
    storageCosts,
    compartmentTree,
    compartmentResources,
    compartmentCosts,
    shapeAnalysis,
    shapeOptimizations
  };
};

// Provider component
export const AppDataProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const isInitialMount = useRef(true);
  const dataLoaded = useRef(false);
  
  // Refs for caching mechanisms
  const apiCache = useRef({});
  const clientDataTimers = useRef({});
  const clientDataFetches = useRef({});
  
  // Generate mock data once
  const mockData = useRef(createMockData());
  
  // Use mock data in development mode
//   const useMockData = process.env.NODE_ENV === 'development';
  const useMockData = false;
  
  // A helper function to create cache keys
  const createCacheKey = (endpoint, params = {}) => {
    const sortedParams = Object.entries(params)
      .filter(([_, value]) => value !== undefined && value !== null && value !== '')
      .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
      .map(([key, value]) => `${key}=${value}`)
      .join('&');
      
    return `${endpoint}${sortedParams ? `?${sortedParams}` : ''}`;
  };
  
  // A helper function to get data from cache or fetch from API
  const getCachedOrFetch = useCallback(async (endpoint, params = {}, maxAge = 5 * 60 * 1000) => {
    const cacheKey = createCacheKey(endpoint, params);
    
    // Check if we have a cached result
    if (apiCache.current[cacheKey]) {
      const { data, timestamp } = apiCache.current[cacheKey];
      const age = Date.now() - timestamp;
      
      // Use cached data if it's not too old
      if (age < maxAge) {
        //console.log(`Using cached data for ${cacheKey} (${Math.round(age/1000)}s old)`);
        return data;
      }
    }
    
    // Build the URL with query parameters
    let url = endpoint;
    const queryParams = Object.entries(params)
      .filter(([_, value]) => value !== undefined && value !== null && value !== '')
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join('&');
      
    if (queryParams) {
      url += `?${queryParams}`;
    }
    
    // Fetch data from API using getWithAuth instead of get to ensure headers are sent
    //console.log(`Fetching data from ${url}`);
    const response = await api.getWithAuth(url);
    
    // Cache the result
    apiCache.current[cacheKey] = {
      data: response.data,
      timestamp: Date.now()
    };
    
    return response.data;
  }, []);
  
  // Function to update the filter parameters
  const setFilterParams = useCallback((params) => {
    dispatch({ 
      type: ACTIONS.SET_FILTER_PARAMS, 
      payload: params 
    });
  }, []);
  
  // Function to clear cache for a specific endpoint or all cache
  const clearCache = useCallback((endpoint = null) => {
    if (endpoint) {
      // Clear all entries that start with the endpoint
      Object.keys(apiCache.current).forEach(key => {
        if (key.startsWith(endpoint)) {
          delete apiCache.current[key];
        }
      });
      //console.log(`Cache cleared for ${endpoint}`);
    } else {
      // Clear all cache
      apiCache.current = {};
      //console.log('All cache cleared');
    }
  }, []);
  
  const fetchAlerts = useCallback(async (createTickets = false, addEsdlAlerts = true) => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'alerts', value: true } });
    
    try {
      if (useMockData) {
        // Generate mock alerts with timestamps matching MongoDB format
        const now = new Date();
        
        // Create timestamps at different intervals in the past
        const timestamp1 = new Date(now).toISOString();
        const timestamp2 = new Date(now.getTime() - 15 * 60000).toISOString(); // 15 min ago
        const timestamp3 = new Date(now.getTime() - 45 * 60000).toISOString(); // 45 min ago
        const timestamp4 = new Date(now.getTime() - 3 * 3600000).toISOString(); // 3 hours ago
        const timestamp5 = new Date(now.getTime() - 8 * 3600000).toISOString(); // 8 hours ago
        
        const mockAlerts = [
          {
            hostname: 'RUH-LISTENER-02',
            region: 'RUH',
            type: 'cpu',
            value: 92.5,
            threshold: 85,
            message: 'High CPU usage (92.5%) on RUH-LISTENER-02',
            timestamp: timestamp1
          },
          {
            hostname: 'auh-listener-1',
            region: 'AUH',
            type: 'memory',
            value: 90.3,
            threshold: 85,
            message: 'High memory usage (90.3%) on auh-listener-1',
            timestamp: timestamp2
          },
          {
            hostname: 'sin-listener-01',
            region: 'SIN',
            type: 'disk',
            value: 89.7,
            threshold: 85,
            message: 'High disk usage (89.7%) on sin-listener-01',
            timestamp: timestamp3
          },
          {
            hostname: 'jnb-server-02',
            region: 'JNB',
            type: 'memory',
            value: 95.2,
            threshold: 85,
            message: 'High memory usage (95.2%) on jnb-server-02',
            timestamp: timestamp4
          },
          {
            hostname: 'dxb-listener-01',
            region: 'DXB',
            type: 'cpu',
            value: 98.1,
            threshold: 85,
            message: 'High CPU usage (98.1%) on dxb-listener-01',
            timestamp: timestamp5
          }
        ];
        
        // Generate ESDL critical alerts from any hosts currently in the system
        // These would be added from our custom hook, but we're adding them here for mock data
        if (addEsdlAlerts && mockData.current && mockData.current.esdl_hosts) {
          const criticalHosts = mockData.current.esdl_hosts.filter(host => 
            host.cpu_load > 80 || host.memory_percent > 85 || host.disk_percent > 90
          );
          
          criticalHosts.forEach(host => {
            // Add CPU alert for hosts with high CPU
            if (host.cpu_load > 80) {
              mockAlerts.push({
                hostname: host.hostname,
                region: host.region,
                type: 'cpu',
                value: host.cpu_load,
                threshold: 80,
                message: `High CPU usage (${host.cpu_load.toFixed(1)}%) on ${host.hostname}`,
                timestamp: new Date().toISOString()
              });
            }
            
            // Add memory alert for hosts with high memory
            if (host.memory_percent > 85) {
              mockAlerts.push({
                hostname: host.hostname,
                region: host.region,
                type: 'memory',
                value: host.memory_percent,
                threshold: 85,
                message: `High memory usage (${host.memory_percent.toFixed(1)}%) on ${host.hostname}`,
                timestamp: new Date().toISOString()
              });
            }
            
            // Add disk alert for hosts with high disk
            if (host.disk_percent > 90) {
              mockAlerts.push({
                hostname: host.hostname,
                region: host.region,
                type: 'disk',
                value: host.disk_percent,
                threshold: 90,
                message: `High disk usage (${host.disk_percent.toFixed(1)}%) on ${host.hostname}`,
                timestamp: new Date().toISOString()
              });
            }
          });
        }
        
        // Sort alerts by timestamp (newest first)
        mockAlerts.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        
        dispatch({ type: ACTIONS.SET_ALERTS, payload: mockAlerts });
        dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'alerts', value: false } });
        return mockAlerts;
      } else {
        // Get data with shorter cache TTL for alerts (1 minute)
        const params = createTickets ? { create_tickets: true } : {};
        const data = await getCachedOrFetch('/api/alerts', params, 60 * 1000);
        const alerts = data.alerts || [];
        
        // Mark ESDL alerts explicitly
        const updatedAlerts = alerts.map(alert => {
          if (['cpu', 'memory', 'disk'].includes(alert.type)) {
            return {
              ...alert,
              source: 'ESDL'
            };
          }
          return alert;
        });
        
        dispatch({ type: ACTIONS.SET_ALERTS, payload: updatedAlerts });
        dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'alerts', value: false } });
        
        // If tickets were created, return those too
        if (data.created_tickets) {
          return {
            alerts: updatedAlerts,
            created_tickets: data.created_tickets
          };
        }
        
        return updatedAlerts;
      }
    } catch (error) {
      console.error('Error fetching alerts:', error);
      dispatch({ type: ACTIONS.SET_ERROR, payload: 'Failed to fetch alerts' });
      dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'alerts', value: false } });
      return [];
    }
  }, [useMockData, getCachedOrFetch]);
  
  // Function to fetch regions from database
  const fetchRegions = useCallback(async () => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'regions', value: true } });
    
    try {
      // Regions rarely change, use a longer cache TTL (1 hour)
      const data = await getCachedOrFetch('/api/auth/regions', {}, 60 * 60 * 1000);
      
      // Store the region data
      const regionData = data.regions || [];
      
      // Update regions in state using the dispatch
      const regionCodes = regionData.map(r => r.code);
      dispatch({ type: ACTIONS.SET_REGIONS, payload: regionCodes });
      dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'regions', value: false } });
      
      return regionCodes;
    } catch (error) {
      console.error('Error fetching regions:', error);
      dispatch({ type: ACTIONS.SET_ERROR, payload: 'Failed to fetch regions' });
      dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'regions', value: false } });
      return [];
    }
  }, [getCachedOrFetch]);

  // Function to fetch hosts
  const fetchHosts = useCallback(async (region = '') => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'hosts', value: true } });
    
    try {
      if (useMockData) {
        // Use mock data
        setTimeout(() => {
          let filteredHosts = [...mockData.current.hosts];
          if (region) {
            filteredHosts = filteredHosts.filter(host => host.startsWith(region));
          }
          dispatch({ type: ACTIONS.SET_HOSTS, payload: filteredHosts });
          dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'hosts', value: false } });
        }, 300);
        return mockData.current.hosts;
      } else {
        // Get hosts with cache (10 minutes TTL)
        const data = await getCachedOrFetch('/api/hosts', { region }, 10 * 60 * 1000);
        const hosts = data.hosts || [];
        
        dispatch({ type: ACTIONS.SET_HOSTS, payload: hosts });
        dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'hosts', value: false } });
        return hosts;
      }
    } catch (error) {
      console.error('Error fetching hosts:', error);
      dispatch({ type: ACTIONS.SET_ERROR, payload: 'Failed to fetch hosts' });
      dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'hosts', value: false } });
      return [];
    }
  }, [useMockData, getCachedOrFetch]);

  // Function to fetch clients
  const fetchClients = useCallback(async (region = '', params = {}) => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'clients', value: true } });
    
    const { from_date = '', to_date = '' } = params;
    
    try {
      if (useMockData) {
        // Use mock data
        setTimeout(() => {
          dispatch({ type: ACTIONS.SET_CLIENTS, payload: mockData.current.clients });
          dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'clients', value: false } });
        }, 300);
        return mockData.current.clients;
      } else {
        // Get clients with cache (5 minutes TTL)
        const apiParams = { region, from_date, to_date };
        const data = await getCachedOrFetch('/api/clients', apiParams, 5 * 60 * 1000);
        
        // Update state with the client data
        dispatch({ type: ACTIONS.SET_CLIENTS, payload: data.clients });
        dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'clients', value: false } });
        
        // Update filter params to reflect current selection
        setFilterParams({ region, from_date, to_date });
        
        return data.clients;
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
      dispatch({ type: ACTIONS.SET_ERROR, payload: 'Failed to fetch clients' });
      dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'clients', value: false } });
      return [];
    }
  }, [useMockData, getCachedOrFetch, setFilterParams]);
  
  // For filtering parent clients for dashboard display
  const getParentClientsCount = useCallback((clients) => {
    if (!clients || !clients.length) return 0;
    
    // Define parent patterns
    const PARENT_PATTERNS = ['DICT', 'IFFCO', 'ABP', 'SF', 'XL'];
    
    // Set to track child clients
    const childClientsList = new Set();
    const parentsList = new Set();
    
    // First identify all child clients
    clients.forEach(clientName => {
      // Check if this client belongs to any parent pattern
      for (const pattern of PARENT_PATTERNS) {
        if (clientName !== pattern && 
            clientName.toLowerCase().startsWith(pattern.toLowerCase())) {
          // This is a child client
          childClientsList.add(clientName);
          // Add its parent to the parents list
          parentsList.add(pattern);
          break;
        }
      }
      
      // Check if it's a child of other clients
      for (const potentialParent of clients) {
        // Skip if it's the same client or a known parent pattern
        if (potentialParent === clientName || PARENT_PATTERNS.includes(potentialParent)) {
          continue;
        }
        
        // Check if this client is a child of potentialParent
        if (clientName !== potentialParent && 
            clientName.toLowerCase().startsWith(potentialParent.toLowerCase())) {
          childClientsList.add(clientName);
          parentsList.add(potentialParent);
          break;
        }
      }
    });
    
    // Count standalone clients (those not in childClientsList)
    const standaloneCount = clients.filter(
      clientName => !childClientsList.has(clientName) && !PARENT_PATTERNS.includes(clientName)
    ).length;
    
    // Total is the number of parent patterns plus standalone clients
    return parentsList.size + standaloneCount;
  }, []);
  
  // Function to fetch summary - now uses clients data from state or cache
  const fetchSummary = useCallback(async (region = '') => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'summary', value: true } });
    
    try {
      if (useMockData) {
        // Use mock data
        setTimeout(() => {
          dispatch({ type: ACTIONS.SET_SUMMARY, payload: mockData.current.summary });
          dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'summary', value: false } });
        }, 300);
        return mockData.current.summary;
      } else {
        // Get summary from API with caching
        const summaryData = await getCachedOrFetch('/api/summary', { region }, 5 * 60 * 1000);
        
        // Get current clients from state or fetch them if not available
        let clientsList = state.clients;
        if (!clientsList || clientsList.length === 0) {
          // Check cache before making a new API call
          const cachedClients = apiCache.current[createCacheKey('/api/clients', { region })];
          if (cachedClients && (Date.now() - cachedClients.timestamp < 5 * 60 * 1000)) {
            clientsList = cachedClients.data.clients || [];
          } else {
            // If not in state or cache, we need to fetch
            // This should be rare since fetchClients is usually called before fetchSummary
            clientsList = await fetchClients(region);
          }
        }
        
        // Filter clients to exclude system clients
        const filteredClients = clientsList.filter(client => 
          !isExcludedInternalClient(client) &&
          !client.toLowerCase().includes('listener') &&
          client !== 'EXTRA' &&
          client !== 'log_size_monitor' &&
          client !== 'rsyslog_size_history' &&
          client !== 'disk_usage_monitor' &&
          client !== 'errorlogs' &&
          client !== 'Advisory' &&
          client !== 'rsyslog1'
        );
        
        // Get parent view client count
        const parentViewCount = getParentClientsCount(filteredClients);
        
        // Add filtered client count to the summary
        const summaryWithFiltered = {
          ...summaryData,
          total_clients_filtered: filteredClients.length,
          parent_view_count: parentViewCount
        };
        
        dispatch({ type: ACTIONS.SET_SUMMARY, payload: summaryWithFiltered });
        dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'summary', value: false } });
        return summaryWithFiltered;
      }
    } catch (error) {
      console.error('Error fetching summary:', error);
      dispatch({ type: ACTIONS.SET_ERROR, payload: 'Failed to fetch summary' });
      dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'summary', value: false } });
      return initialState.summary;
    }
  }, [useMockData, getCachedOrFetch, getParentClientsCount, state.clients, fetchClients]);
  
  // Function to fetch system metrics
  const fetchSystemMetrics = useCallback(async (params = {}) => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'systemMetrics', value: true } });
    
    const { range = '24h', region = '', host = '' } = params;
    
    try {
      if (useMockData) {
        // Use mock data
        setTimeout(() => {
          dispatch({ type: ACTIONS.SET_SYSTEM_METRICS, payload: mockData.current.systemMetrics });
          dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'systemMetrics', value: false } });
        }, 500);
        return mockData.current.systemMetrics;
      } else {
        // Get system metrics with cache
        const apiParams = { range, region, host };
        const data = await getCachedOrFetch('/api/system_metrics', apiParams, 5 * 60 * 1000);
        
        dispatch({ type: ACTIONS.SET_SYSTEM_METRICS, payload: data });
        dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'systemMetrics', value: false } });
        return data;
      }
    } catch (error) {
      console.error('Error fetching system metrics:', error);
      dispatch({ type: ACTIONS.SET_ERROR, payload: 'Failed to fetch system metrics' });
      dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'systemMetrics', value: false } });
      return { cpu: [], memory: [] };
    }
  }, [useMockData, getCachedOrFetch]);
  
  // Function to fetch system metrics for hosts
  const fetchSystemMetricsForHosts = useCallback(async (hostList) => {
    if (!hostList || hostList.length === 0) return {};
    
    dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'systemMetrics', value: true } });
    
    try {
      if (useMockData) {
        // Return pre-generated host metrics
        setTimeout(() => {
          dispatch({ type: ACTIONS.SET_SYSTEM_METRICS, payload: mockData.current.hostMetrics });
          dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'systemMetrics', value: false } });
        }, 500);
        
        return mockData.current.hostMetrics;
      } else {
        // Real API calls - batch by region
        const hostsByRegion = {};
        
        hostList.forEach(host => {
          const region = host.split('-')[0];
          if (!hostsByRegion[region]) {
            hostsByRegion[region] = [];
          }
          hostsByRegion[region].push(host);
        });
        
        const requests = Object.entries(hostsByRegion).map(([region, hosts]) => {
          return getCachedOrFetch('/api/system_metrics_batch', { 
            hosts: hosts.join(','), 
            region 
          }, 5 * 60 * 1000);
        });
        
        const responses = await Promise.all(requests);
        const metricsData = {};
        
        responses.forEach(data => {
          Object.assign(metricsData, data);
        });
        
        dispatch({ type: ACTIONS.SET_SYSTEM_METRICS, payload: metricsData });
        dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'systemMetrics', value: false } });
        return metricsData;
      }
    } catch (error) {
      console.error('Error fetching system metrics for hosts:', error);
      
      // Return mock data as fallback even in production to prevent UI errors
      const fallbackMetrics = {};
      
      hostList.forEach(hostname => {
        const region = hostname.split('-')[0];
        fallbackMetrics[hostname] = {
          cpu: 50,
          memory: 60,
          disk: 70,
          status: 'active',
          region: region,
        };
      });
      
      dispatch({ type: ACTIONS.SET_SYSTEM_METRICS, payload: fallbackMetrics });
      dispatch({ type: ACTIONS.SET_ERROR, payload: 'Failed to fetch system metrics for hosts' });
      dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'systemMetrics', value: false } });
      return fallbackMetrics;
    }
  }, [useMockData, getCachedOrFetch]);
  
  // Function to fetch client logs data with improved caching
  const fetchClientLogs = useCallback(async (params = {}) => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'clientLogs', value: true } });
    
    const { range = '24h', region = '', host = '', client = '', product = '', device_id = '', from_date = '', to_date = '', forceRefresh = false } = params;
    
    // Create a cache key based on the parameters (for log messages only)
    const cacheKey = `client_logs_${range}_${region}_${host}_${client}_${product}_${device_id}_${from_date}_${to_date}`;
    
    // Always show loading indicator as we're not using cache
    dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'clientLogs', value: true } });
    
    try {
      // For large date ranges, show a loading indicator with estimated completion time
      if (from_date && to_date) {
        const startDate = new Date(from_date);
        const endDate = new Date(to_date);
        const daysInRange = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
        
        if (daysInRange > 14) {
          // This will be a long query - consider warning the user
          console.warn(`Large date range query (${daysInRange} days) may take several minutes to complete`);
          
          // Display a partial loading state that shows progress
          dispatch({ 
            type: ACTIONS.SET_CLIENT_LOGS, 
            payload: {
              by_client: [],
              by_product: [],
              timeseries: [],
              loading_large_dataset: true,
              date_range: { from: from_date, to: to_date },
              days_in_range: daysInRange,
              estimated_seconds: Math.min(daysInRange * 6, 300) // Rough estimate of time needed
            }
          });
        }
      }
      
      // Real API call with date range parameters if provided
      let url = '/api/client_logs?';
      
      // Use date range if provided, otherwise use range
      if (from_date && to_date) {
        url += `from_date=${from_date}&to_date=${to_date}`;
      } else {
        url += `range=${range}`;
      }
      
      // Add other parameters
      if (region) url += `&region=${region}`;
      if (host) url += `&host=${host}`;
      if (client) url += `&client=${client}`;
      if (product) url += `&product=${product}`;
      if (device_id) url += `&device_id=${device_id}`;
      
      // Always add nocache=true to force fresh data from the backend
      url += `&nocache=true`;
      
      // Start measuring request time
      const requestStartTime = Date.now();
      
      // Make the API call
      const response = await api.get(url);
      
      // Calculate request time
      const requestTime = Date.now() - requestStartTime;
      //console.log(`Client logs request completed in ${requestTime}ms (real-time data)`);
      
      dispatch({ type: ACTIONS.SET_CLIENT_LOGS, payload: response.data });
      dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'clientLogs', value: false } });
      
      // Update filter params
      setFilterParams({ region, from_date, to_date });
      
      return response.data;
      
    } catch (error) {
      console.error('Error fetching client logs:', error);
      dispatch({ type: ACTIONS.SET_ERROR, payload: 'Failed to fetch client logs' });
      dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'clientLogs', value: false } });
      return { by_client: [], by_product: [], timeseries: [] };
    }
  }, [setFilterParams]);

  const fetchCompartmentResources = useCallback(async (params = {}) => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'compartmentResources', value: true } });
    
    const { region = '', date = new Date().toISOString().split('T')[0] } = params;
    
    try {
      if (useMockData) {
        // Use mock data
        setTimeout(() => {
          dispatch({ type: ACTIONS.SET_COMPARTMENT_RESOURCES, payload: mockData.current.compartmentResources });
          dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'compartmentResources', value: false } });
        }, 300);
        return mockData.current.compartmentResources;
      } else {
        // Get compartment resources with caching
        const apiParams = { region, date };
        const data = await getCachedOrFetch('/api/oci/compartments/resources', apiParams, 30 * 60 * 1000);
        
        dispatch({ type: ACTIONS.SET_COMPARTMENT_RESOURCES, payload: data });
        dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'compartmentResources', value: false } });
        return data;
      }
    } catch (error) {
      console.error('Error fetching compartment resources:', error);
      dispatch({ type: ACTIONS.SET_ERROR, payload: 'Failed to fetch compartment resources' });
      dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'compartmentResources', value: false } });
      return [];
    }
  }, [useMockData, getCachedOrFetch]);

  const fetchCompartmentCosts = useCallback(async (params = {}) => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'compartmentCosts', value: true } });
    
    const { region = '', date = new Date().toISOString().split('T')[0] } = params;
    
    try {
      if (useMockData) {
        // Use mock data
        setTimeout(() => {
          dispatch({ type: ACTIONS.SET_COMPARTMENT_COSTS, payload: mockData.current.compartmentCosts });
          dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'compartmentCosts', value: false } });
        }, 300);
        return mockData.current.compartmentCosts;
      } else {
        // Get compartment costs with caching (30 minutes TTL as it rarely changes)
        const apiParams = { region, date };
        const data = await getCachedOrFetch('/api/oci/compartments/costs', apiParams, 30 * 60 * 1000);
        
        dispatch({ type: ACTIONS.SET_COMPARTMENT_COSTS, payload: data });
        dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'compartmentCosts', value: false } });
        return data;
      }
    } catch (error) {
      console.error('Error fetching compartment costs:', error);
      dispatch({ type: ACTIONS.SET_ERROR, payload: 'Failed to fetch compartment costs' });
      dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'compartmentCosts', value: false } });
      return [];
    }
  }, [useMockData, getCachedOrFetch]);

  // VM Shape Analysis functions
  const fetchShapeAnalysis = useCallback(async (params = {}) => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'shapeAnalysis', value: true } });
    
    const { region = '', compartment = '', date = new Date().toISOString().split('T')[0] } = params;
    
    try {
      if (useMockData) {
        // Use mock data
        setTimeout(() => {
          dispatch({ type: ACTIONS.SET_SHAPE_ANALYSIS, payload: mockData.current.shapeAnalysis });
          dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'shapeAnalysis', value: false } });
        }, 300);
        return mockData.current.shapeAnalysis;
      } else {
        // Get shape analysis with caching (15 minutes TTL)
        const apiParams = { region, compartment, date };
        const data = await getCachedOrFetch('/api/oci/shapes/analysis', apiParams, 15 * 60 * 1000);
        
        dispatch({ type: ACTIONS.SET_SHAPE_ANALYSIS, payload: data });
        dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'shapeAnalysis', value: false } });
        return data;
      }
    } catch (error) {
      console.error('Error fetching shape analysis:', error);
      dispatch({ type: ACTIONS.SET_ERROR, payload: 'Failed to fetch shape analysis' });
      dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'shapeAnalysis', value: false } });
      return { shapes: [], instances: [], summary: {} };
    }
  }, [useMockData, getCachedOrFetch]);

  const fetchShapeOptimizations = useCallback(async (params = {}) => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'shapeOptimizations', value: true } });
    
    const { region = '', compartment = '', date = new Date().toISOString().split('T')[0] } = params;
    
    try {
      if (useMockData) {
        // Use mock data
        setTimeout(() => {
          dispatch({ type: ACTIONS.SET_SHAPE_OPTIMIZATIONS, payload: mockData.current.shapeOptimizations });
          dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'shapeOptimizations', value: false } });
        }, 300);
        return mockData.current.shapeOptimizations;
      } else {
        // Get shape optimizations with caching (15 minutes TTL)
        const apiParams = { region, compartment, date };
        const data = await getCachedOrFetch('/api/oci/shapes/optimizations', apiParams, 15 * 60 * 1000);
        
        dispatch({ type: ACTIONS.SET_SHAPE_OPTIMIZATIONS, payload: data });
        dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'shapeOptimizations', value: false } });
        return data;
      }
    } catch (error) {
      console.error('Error fetching shape optimizations:', error);
      dispatch({ type: ACTIONS.SET_ERROR, payload: 'Failed to fetch shape optimizations' });
      dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'shapeOptimizations', value: false } });
      return { recommendations: [], summary: {} };
    }
  }, [useMockData, getCachedOrFetch]);

  // Function to fetch client count data
  const fetchClientCount = useCallback(async (params = {}) => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'clientCount', value: true } });
    
    const { region = '', from_date = '', to_date = '' } = params;
    
    try {
      if (useMockData) {
        // Use mock data
        setTimeout(() => {
          dispatch({ type: ACTIONS.SET_CLIENT_COUNT, payload: mockData.current.clientCount });
          dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'clientCount', value: false } });
        }, 300);
        return mockData.current.clientCount;
      } else {
        // Get client count with caching (5 minutes TTL)
        const apiParams = { region, from_date, to_date };
        const data = await getCachedOrFetch('/api/client_product_count', apiParams, 5 * 60 * 1000);
        
        dispatch({ type: ACTIONS.SET_CLIENT_COUNT, payload: data });
        dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'clientCount', value: false } });
        return data;
      }
    } catch (error) {
      console.error('Error fetching client count:', error);
      dispatch({ type: ACTIONS.SET_ERROR, payload: 'Failed to fetch client count' });
      dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'clientCount', value: false } });
      return { regions: {} };
    }
  }, [useMockData, getCachedOrFetch]);
  
  // Function to fetch host clients data
  const fetchHostClients = useCallback(async (hostname) => {
    if (!hostname) return;
    
    dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'hostClients', value: true } });
    
    try {
      if (useMockData) {
        // Use mock data
        setTimeout(() => {
          const clients = mockData.current.clients.slice(0, 3);
          dispatch({ type: ACTIONS.SET_HOST_CLIENTS, payload: clients });
          dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'hostClients', value: false } });
        }, 300);
        return mockData.current.clients.slice(0, 3);
      } else {
        // Get host clients with caching (10 minutes TTL)
        const data = await getCachedOrFetch(`/api/host/${hostname}/clients`, {}, 10 * 60 * 1000);
        
        dispatch({ type: ACTIONS.SET_HOST_CLIENTS, payload: data.clients });
        dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'hostClients', value: false } });
        return data.clients;
      }
    } catch (error) {
      console.error(`Error fetching clients for host ${hostname}:`, error);
      dispatch({ type: ACTIONS.SET_ERROR, payload: `Failed to fetch clients for host ${hostname}` });
      dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'hostClients', value: false } });
      return [];
    }
  }, [useMockData, getCachedOrFetch]);
  
  // Function to fetch client details
  const fetchClientDetails = useCallback(async (clientName) => {
    if (!clientName) return;
    
    dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'clientDetails', value: true } });
    
    try {
      if (useMockData) {
        // Use mock data
        setTimeout(() => {
          const clientData = {
            name: clientName,
            license: {
              start_date: '2023-01-01',
              end_date: '2023-12-31',
              status: 'active'
            },
            hosts: mockData.current.hosts.slice(0, 2),
            products: ['Product A', 'Product B'],
            total_size: Math.floor(Math.random() * 50) + 10
          };
          
          dispatch({ type: ACTIONS.SET_CLIENT_DETAILS, payload: { [clientName]: clientData } });
          dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'clientDetails', value: false } });
        }, 300);
        
        return {
          name: clientName,
          license: {
            start_date: '2023-01-01',
            end_date: '2023-12-31',
            status: 'active'
          },
          hosts: mockData.current.hosts.slice(0, 2),
          products: ['Product A', 'Product B'],
          total_size: Math.floor(Math.random() * 50) + 10
        };
      } else {
        // Get client details with caching (15 minutes TTL)
        const data = await getCachedOrFetch(`/api/client/${clientName}/details`, {}, 15 * 60 * 1000);
        
        // Store in state with client name as key
        dispatch({ type: ACTIONS.SET_CLIENT_DETAILS, payload: { [clientName]: data } });
        dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'clientDetails', value: false } });
        return data;
      }
    } catch (error) {
      console.error(`Error fetching details for client ${clientName}:`, error);
      dispatch({ type: ACTIONS.SET_ERROR, payload: `Failed to fetch details for client ${clientName}` });
      dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'clientDetails', value: false } });
      return null;
    }
  }, [useMockData, getCachedOrFetch]);
  
  // Function to fetch client products
  const fetchClientProducts = useCallback(async (clientName, region = '', params = {}) => {
    if (!clientName) return null;
    
    dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'clientDetails', value: true } });
    
    const { from_date = '', to_date = '' } = params;
    
    // Create a cache key based on the parameters
    const cacheKey = `client_products_${clientName}_${region}_${from_date}_${to_date}`;
    const cachedData = sessionStorage.getItem(cacheKey);
    
    // Check if we have cached data and it's less than 5 minutes old
    if (cachedData) {
      try {
        const { data, timestamp } = JSON.parse(cachedData);
        const age = Date.now() - timestamp;
        
        // Use cached data if less than 5 minutes old
        if (age < 5 * 60 * 1000) {
          //console.log('Using cached client products data');
          dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'clientDetails', value: false } });
          return data;
        }
      } catch (e) {
        console.error('Error parsing cached data:', e);
      }
    }
    
    try {
      if (useMockData) {
        // Use mock data
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // Generate mock client product data
        const mockClientProducts = {
          client_name: clientName,
          logs: {
            by_product: Array.from({ length: 5 }, (_, i) => ({
              client_name: clientName,
              product: `Product${i+1}`,
              size_gb: Math.floor(Math.random() * 100) + 5
            }))
          }
        };
        
        dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'clientDetails', value: false } });
        return mockClientProducts;
      } else {
        // Real API call with support for region and date parameters
        let url = `/api/clients/${clientName}/products`;
        
        // Add query parameters
        const queryParams = [];
        if (region) queryParams.push(`region=${region}`);
        if (from_date) queryParams.push(`from_date=${from_date}`);
        if (to_date) queryParams.push(`to_date=${to_date}`);
        
        // Add query parameters to URL if any exist
        if (queryParams.length > 0) {
          url += `?${queryParams.join('&')}`;
        }
        
        console.log(`Fetching client products from: ${url}`);
        const response = await api.get(url);
        
        // Special handling for DICT and other parent clients
        // Check if this is a parent client that needs child data aggregation
        const PARENT_PATTERNS = ['DICT', 'IFFCO', 'ABP', 'SF', 'XL'];
        if (PARENT_PATTERNS.includes(clientName)) {
          console.log(`${clientName} is a parent client - fetching child data`);
          
          // Get all clients to find children
          const clientsResponse = await api.get('/api/clients');
          const allClients = clientsResponse.data.clients || [];
          
          // Find child clients
          const childClients = allClients.filter(childName => 
            childName !== clientName && 
            childName.toLowerCase().startsWith(clientName.toLowerCase())
          );
          
          console.log(`Found ${childClients.length} children for ${clientName}`);
          
          // If we have children, fetch their data and aggregate
          if (childClients.length > 0) {
            // Aggregate child data with parent data
            let aggregatedProducts = [...(response.data.logs?.by_product || [])];
            
            // Fetch data for each child client
            for (const childName of childClients) {
              let childUrl = `/api/clients/${childName}/products`;
              
              // Add same query parameters
              if (queryParams.length > 0) {
                childUrl += `?${queryParams.join('&')}`;
              }
              
              try {
                console.log(`Fetching data for child client: ${childUrl}`);
                const childResponse = await api.get(childUrl);
                
                // Add child products to aggregated data
                if (childResponse.data.logs?.by_product) {
                  aggregatedProducts = [...aggregatedProducts, ...childResponse.data.logs.by_product];
                }
              } catch (childError) {
                console.error(`Error fetching data for child ${childName}:`, childError);
              }
            }
            
            // Update response data with aggregated products
            response.data.logs = {
              ...response.data.logs,
              by_product: aggregatedProducts,
              is_aggregated: true,
              child_count: childClients.length
            };
            
            console.log(`Aggregated ${aggregatedProducts.length} products for ${clientName}`);
          }
        }
        
        // Cache the result for 5 minutes
        try {
          sessionStorage.setItem(cacheKey, JSON.stringify({
            data: response.data,
            timestamp: Date.now()
          }));
        } catch (e) {
          console.error('Error caching client products:', e);
        }
        
        // Store in state with client name as key
        dispatch({ 
          type: ACTIONS.SET_CLIENT_DETAILS, 
          payload: { 
            [clientName]: {
              ...state.clientDetails[clientName],
              products: response.data
            }
          } 
        });
        
        dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'clientDetails', value: false } });
        return response.data;
      }
    } catch (error) {
      console.error(`Error fetching products for client ${clientName}:`, error);
      dispatch({ type: ACTIONS.SET_ERROR, payload: `Failed to fetch products for client ${clientName}` });
      dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'clientDetails', value: false } });
      return null;
    }
  }, [useMockData, state.clientDetails]);

  // Function to fetch product device IDs
  const fetchProductDeviceIds = useCallback(async (clientName, productName, region = '', params = {}) => {
    if (!clientName || !productName) return null;
    
    dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'deviceIds', value: true } });
    
    const { from_date = '', to_date = '' } = params;
    
    // Create a cache key based on the parameters
    const cacheKey = `product_device_ids_${clientName}_${productName}_${region}_${from_date}_${to_date}`;
    const cachedData = sessionStorage.getItem(cacheKey);
    
    // Check if we have cached data and it's less than 5 minutes old
    if (cachedData) {
      try {
        const { data, timestamp } = JSON.parse(cachedData);
        const age = Date.now() - timestamp;
        
        // Use cached data if less than 5 minutes old
        if (age < 5 * 60 * 1000) {
          //console.log('Using cached product device IDs data');
          dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'deviceIds', value: false } });
          return data;
        }
      } catch (e) {
        console.error('Error parsing cached device IDs data:', e);
      }
    }
    
    try {
      if (useMockData) {
        // Use mock data
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // Generate mock device IDs with data
        const mockDeviceData = Array.from({ length: 3 }, (_, i) => ({
          device_id: `device-${productName}-${i+1}`,
          total_size_gb: Math.floor(Math.random() * 10) + 1,
          hostname: `host-${i+1}`,
          last_seen: new Date().toISOString()
        }));
        
        const mockResult = {
          client_name: clientName,
          product: productName,
          device_data: mockDeviceData
        };
        
        dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'deviceIds', value: false } });
        return mockResult;
      } else {
        // Use the new dedicated endpoint for client product device IDs
        let url = `/api/client_product_device_ids`;
        
        // Add query parameters
        const queryParams = [
          `client=${encodeURIComponent(clientName)}`,
          `product=${encodeURIComponent(productName)}`
        ];
        if (region) queryParams.push(`region=${encodeURIComponent(region)}`);
        if (from_date) queryParams.push(`from_date=${encodeURIComponent(from_date)}`);
        if (to_date) queryParams.push(`to_date=${encodeURIComponent(to_date)}`);
        
        // Add query parameters to URL
        url += `?${queryParams.join('&')}`;
        
        //console.log(`Fetching device IDs for client ${clientName} and product ${productName}`);
        const response = await api.get(url);
        
        //console.log('API response for device IDs:', response.data);
        
        // Process the response data
        const result = {
          client_name: clientName,
          product: productName,
          device_data: response.data.device_data || []
        };
        
        // Cache the result for 5 minutes
        try {
          sessionStorage.setItem(cacheKey, JSON.stringify({
            data: result,
            timestamp: Date.now()
          }));
        } catch (e) {
          console.error('Error caching product device IDs:', e);
        }
        
        dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'deviceIds', value: false } });
        return result;
      }
    } catch (error) {
      console.error(`Error fetching device IDs for client ${clientName} and product ${productName}:`, error);
      dispatch({ type: ACTIONS.SET_ERROR, payload: `Failed to fetch device IDs for product ${productName}` });
      dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'deviceIds', value: false } });
      return null;
    }
  }, [useMockData]);

  // Function to fetch storage details
  const fetchStorageDetails = useCallback(async (params = {}) => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'storageDetails', value: true } });
    
    const { region = '', compartment = '', date = new Date().toISOString().split('T')[0] } = params;
    
    try {
      if (useMockData) {
        // Use mock data
        setTimeout(() => {
          dispatch({ type: ACTIONS.SET_STORAGE_DETAILS, payload: mockData.current.storageDetails });
          dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'storageDetails', value: false } });
        }, 300);
        return mockData.current.storageDetails;
      } else {
        // Get storage details with caching (30 minutes TTL)
        const apiParams = { region, compartment, date };
        const data = await getCachedOrFetch('/api/oci/storage/details', apiParams, 30 * 60 * 1000);
        
        dispatch({ type: ACTIONS.SET_STORAGE_DETAILS, payload: data });
        dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'storageDetails', value: false } });
        return data;
      }
    } catch (error) {
      console.error('Error fetching storage details:', error);
      dispatch({ type: ACTIONS.SET_ERROR, payload: 'Failed to fetch storage details' });
      dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'storageDetails', value: false } });
      return null;
    }
  }, [useMockData, getCachedOrFetch]);

  // Function to fetch storage trends
  const fetchStorageTrends = useCallback(async (params = {}) => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'storageTrends', value: true } });
    
    const { region = '', days = 30 } = params;
    
    try {
      if (useMockData) {
        // Use mock data
        setTimeout(() => {
          dispatch({ type: ACTIONS.SET_STORAGE_TRENDS, payload: mockData.current.storageTrends });
          dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'storageTrends', value: false } });
        }, 300);
        return mockData.current.storageTrends;
      } else {
        // Get storage trends with caching (15 minutes TTL)
        const apiParams = { region, days };
        const data = await getCachedOrFetch('/api/oci/storage/trends', apiParams, 15 * 60 * 1000);
        
        dispatch({ type: ACTIONS.SET_STORAGE_TRENDS, payload: data });
        dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'storageTrends', value: false } });
        return data;
      }
    } catch (error) {
      console.error('Error fetching storage trends:', error);
      dispatch({ type: ACTIONS.SET_ERROR, payload: 'Failed to fetch storage trends' });
      dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'storageTrends', value: false } });
      return null;
    }
  }, [useMockData, getCachedOrFetch]);

  // Function to fetch storage costs
  const fetchStorageCosts = useCallback(async (params = {}) => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'storageCosts', value: true } });
    
    const { region = '', days = 30 } = params;
    
    try {
      if (useMockData) {
        // Use mock data
        setTimeout(() => {
          dispatch({ type: ACTIONS.SET_STORAGE_COSTS, payload: mockData.current.storageCosts });
          dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'storageCosts', value: false } });
        }, 300);
        return mockData.current.storageCosts;
      } else {
        // Get storage costs with caching (15 minutes TTL)
        const apiParams = { region, days };
        const data = await getCachedOrFetch('/api/oci/storage/costs', apiParams, 15 * 60 * 1000);
        
        dispatch({ type: ACTIONS.SET_STORAGE_COSTS, payload: data });
        dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'storageCosts', value: false } });
        return data;
      }
    } catch (error) {
      console.error('Error fetching storage costs:', error);
      dispatch({ type: ACTIONS.SET_ERROR, payload: 'Failed to fetch storage costs' });
      dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'storageCosts', value: false } });
      return null;
    }
  }, [useMockData, getCachedOrFetch]);

  // Function to fetch compartment tree
  const fetchCompartmentTree = useCallback(async (region = '') => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'compartmentTree', value: true } });
    
    try {
      if (useMockData) {
        // Use mock data
        setTimeout(() => {
          dispatch({ type: ACTIONS.SET_COMPARTMENT_TREE, payload: mockData.current.compartmentTree });
          dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'compartmentTree', value: false } });
        }, 300);
        return mockData.current.compartmentTree;
      } else {
        // Get compartment tree with caching (1 hour TTL - rarely changes)
        const data = await getCachedOrFetch('/api/oci/compartments/tree', { region }, 60 * 60 * 1000);
        
        dispatch({ type: ACTIONS.SET_COMPARTMENT_TREE, payload: data });
        dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'compartmentTree', value: false } });
        return data;
      }
    } catch (error) {
      console.error('Error fetching compartment tree:', error);
      dispatch({ type: ACTIONS.SET_ERROR, payload: 'Failed to fetch compartment tree' });
      dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'compartmentTree', value: false } });
      return [];
    }
  }, [useMockData, getCachedOrFetch]);

  // Function to refresh all data - IMPROVED: uses shared cache and batched calls
  const refreshAllData = useCallback(async (params = {}) => {
    dispatch({ type: ACTIONS.CLEAR_ERROR });
    
    const { region = '', timeRange = '24h', from_date = '', to_date = '' } = params;
    
    // First, update the filter parameters in state
    setFilterParams({ region, timeRange, from_date, to_date });
    
    try {
      // Group API calls into batches for better parallel execution
      // Batch 1: Fast, independent API calls
      const batch1 = [
        fetchRegions(),
        fetchHosts(region),
        fetchClientCount({ region, from_date, to_date }) // Moved client count to batch1 for faster loading
      ];
      
      await Promise.all(batch1);
      
      // Batch 2: Dependencies that need clients and regions
      const batch2 = [
        // This will use the clients already fetched if available
        fetchClients(region, { from_date, to_date }),
        fetchSystemMetrics({ range: timeRange, region }),
      ];
      
      const [clients] = await Promise.all(batch2);
      
      // Batch 3: Calls that depend on clients data
      const batch3 = [
        // Now we can use the client data for summary without making a separate API call
        fetchSummary(region),
        // Always force refresh for client logs to ensure real-time data
        fetchClientLogs({ range: timeRange, region, from_date, to_date, forceRefresh: true }),
        fetchAlerts()
      ];
      
      await Promise.all(batch3);
      
      dataLoaded.current = true;
      return true;
    } catch (error) {
      console.error('Error refreshing data:', error);
      dispatch({ type: ACTIONS.SET_ERROR, payload: 'Failed to refresh data' });
      return false;
    }
  }, [fetchRegions, fetchHosts, fetchClients, fetchSummary, fetchSystemMetrics, fetchClientLogs, fetchClientCount, fetchAlerts, setFilterParams]);

  // Function to refresh all OCI data
  const refreshOCIData = useCallback(async (params = {}) => {
    dispatch({ type: ACTIONS.CLEAR_ERROR });
    
    const { region = '', compartment = '', date = new Date().toISOString().split('T')[0] } = params;
    
    try {
      // Refresh all OCI-related data in parallel
      const promises = [
        fetchRegions(),
        fetchStorageDetails({ region, compartment, date }),
        fetchCompartmentTree(region),
        fetchCompartmentResources({ region, date }),
        fetchCompartmentCosts({ region, date }),
        fetchShapeAnalysis({ region, compartment, date })
      ];
      
      await Promise.all(promises);
      return true;
    } catch (error) {
      console.error('Error refreshing OCI data:', error);
      dispatch({ type: ACTIONS.SET_ERROR, payload: 'Failed to refresh OCI data. Please try again.' });
      return false;
    }
  }, [fetchRegions, fetchStorageDetails, fetchCompartmentTree, fetchCompartmentResources, fetchCompartmentCosts, fetchShapeAnalysis]);

  // Force refresh all data - for manual refreshes
  const forceRefreshAllData = useCallback(async () => {
    //console.log("Force refreshing all data");
    dispatch({ type: ACTIONS.CLEAR_ERROR });
    
    try {
      // Clear cache for frequently changing endpoints
      // Note: We don't clear client_logs cache since we're always fetching real-time data now
      clearCache('/api/clients');
      clearCache('/api/summary');
      clearCache('/api/system_metrics');
      clearCache('/api/alerts');
      
      // Get the selected region from state, localStorage, or default to empty
      const region = state.filterParams.region || localStorage.getItem('selectedRegion') || '';
      const from_date = state.filterParams.from_date || '';
      const to_date = state.filterParams.to_date || '';
      const timeRange = state.filterParams.timeRange || '24h';
      
      // Refresh all data with the current filter parameters
      await refreshAllData({ region, timeRange, from_date, to_date });
      
      return true;
    } catch (error) {
      console.error('Error force refreshing data:', error);
      dispatch({ type: ACTIONS.SET_ERROR, payload: 'Failed to refresh data' });
      return false;
    }
  }, [refreshAllData, clearCache, state.filterParams]);

  // Load initial data once
  useEffect(() => {
    // Only load data on initial mount and when not already loaded
    if (isInitialMount.current && !dataLoaded.current) {
      isInitialMount.current = false;
      
      // Get initial filter parameters from localStorage if available
      const region = localStorage.getItem('selectedRegion') || '';
      const timeRange = localStorage.getItem('selectedTimeRange') || '24h';
      
      // Default to today's date for initial load
      const today = new Date().toISOString().split('T')[0];
      
      refreshAllData({ region, timeRange, from_date: today, to_date: today });
    }
  }, [refreshAllData]);

  // Fetch Jira configuration
  const fetchJiraConfig = useCallback(async () => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'jiraConfig', value: true } });
    
    try {
      if (useMockData) {
        // Mock Jira config
        const mockJiraConfig = {
          jira_enabled: true,
          jira_url: 'https://eventussecurity.atlassian.net',
          project_key: 'SETT',
          regions: ['RUH', 'JED', 'DMM', 'AUH', 'DXB', 'KUW', 'DOH', 'MUS', 'SIN', 'JNB'],
          region_assignees: {
            'RUH': 'john.doe',
            'JED': 'jane.smith',
            'AUH': 'ahmed.malik',
            'DXB': 'sara.khan',
            'SIN': 'li.wei',
            'JNB': 'nelson.mandela',
            'default': 'admin.user'
          }
        };
        
        // Add some delay for realism
        await new Promise(resolve => setTimeout(resolve, 500));
        
        dispatch({ type: ACTIONS.SET_JIRA_CONFIG, payload: {
          jira_enabled: mockJiraConfig.jira_enabled,
          url: mockJiraConfig.jira_url,
          project_key: mockJiraConfig.project_key,
          regions: mockJiraConfig.regions,
          region_assignees: mockJiraConfig.region_assignees
        }});
        
        dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'jiraConfig', value: false } });
        return mockJiraConfig;
      } else {
        // Get Jira config with caching (1 hour TTL - rarely changes)
        const data = await getCachedOrFetch('/api/alerts/jira/config', {}, 60 * 60 * 1000);
        
        //console.log("Raw Jira config from API:", data);
        //console.log("Jira enabled value:", data.jira_enabled);
        //console.log("Jira enabled type:", typeof data.jira_enabled);
        
        // Map response to state structure, ensuring proper boolean conversion for enabled
        const jiraEnabled = Boolean(data.jira_enabled);
        //console.log("Converted jiraEnabled value:", jiraEnabled);
        
        dispatch({ type: ACTIONS.SET_JIRA_CONFIG, payload: {
          jira_enabled: jiraEnabled,
          url: data.jira_url || data.url,
          project_key: data.project_key,
          regions: data.regions || [],
          region_assignees: data.region_assignees || {}
        }});
        
        dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'jiraConfig', value: false } });
        return data;
      }
    } catch (error) {
      console.error('Error fetching Jira config:', error);
      dispatch({ type: ACTIONS.SET_ERROR, payload: 'Failed to fetch Jira config' });
      dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'jiraConfig', value: false } });
      return {
        jira_enabled: false,
        jira_url: null,
        project_key: null,
        regions: [],
        region_assignees: {}
      };
    }
  }, [useMockData, getCachedOrFetch]);
  
  // Fetch alert tickets history
  const fetchAlertTickets = useCallback(async (filters = {}) => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'alertTickets', value: true } });
    
    try {
      if (useMockData) {
        // Mock ticket history
        const now = new Date();
        
        // Create timestamps at different intervals in the past
        const timestamp1 = new Date(now.getTime() - 1 * 3600000).toISOString(); // 1 hour ago
        const timestamp2 = new Date(now.getTime() - 6 * 3600000).toISOString(); // 6 hours ago
        const timestamp3 = new Date(now.getTime() - 12 * 3600000).toISOString(); // 12 hours ago
        const timestamp4 = new Date(now.getTime() - 2 * 24 * 3600000).toISOString(); // 2 days ago
        const timestamp5 = new Date(now.getTime() - 5 * 24 * 3600000).toISOString(); // 5 days ago
        
        const mockTickets = [
          {
            hostname: 'RUH-LISTENER-02',
            region: 'RUH',
            type: 'cpu',
            value: 92.5,
            threshold: 85,
            has_ticket: true,
            ticket_key: 'SETT-123',
            ticket_url: 'https://eventussecurity.atlassian.net/browse/SETT-123',
            created_at: timestamp1
          },
          {
            hostname: 'auh-listener-1',
            region: 'AUH',
            type: 'memory',
            value: 90.3,
            threshold: 85,
            has_ticket: true,
            ticket_key: 'SETT-124',
            ticket_url: 'https://eventussecurity.atlassian.net/browse/SETT-124',
            created_at: timestamp2
          },
          {
            hostname: 'sin-listener-01',
            region: 'SIN',
            type: 'disk',
            value: 89.7,
            threshold: 85,
            has_ticket: true,
            ticket_key: 'SETT-125',
            ticket_url: 'https://eventussecurity.atlassian.net/browse/SETT-125',
            created_at: timestamp3
          },
          {
            hostname: 'jnb-server-02',
            region: 'JNB',
            type: 'memory',
            value: 95.2,
            threshold: 85,
            has_ticket: true,
            ticket_key: 'SETT-126',
            ticket_url: 'https://eventussecurity.atlassian.net/browse/SETT-126',
            created_at: timestamp4
          },
          {
            hostname: 'dxb-listener-01',
            region: 'DXB',
            type: 'cpu',
            value: 98.1,
            threshold: 85,
            has_ticket: true,
            ticket_key: 'SETT-127',
            ticket_url: 'https://eventussecurity.atlassian.net/browse/SETT-127',
            created_at: timestamp5
          }
        ];
        
        dispatch({ type: ACTIONS.SET_ALERT_TICKETS, payload: mockTickets });
        dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'alertTickets', value: false } });
        return mockTickets;
      } else {
        // Get alert tickets with caching (5 minutes TTL)
        const data = await getCachedOrFetch('/api/alerts/tickets', filters, 5 * 60 * 1000);
        const tickets = data.tickets || [];
        
        dispatch({ type: ACTIONS.SET_ALERT_TICKETS, payload: tickets });
        dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'alertTickets', value: false } });
        return tickets;
      }
    } catch (error) {
      console.error('Error fetching alert tickets:', error);
      dispatch({ type: ACTIONS.SET_ERROR, payload: 'Failed to fetch alert tickets' });
      dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'alertTickets', value: false } });
      return [];
    }
  }, [useMockData, getCachedOrFetch]);
  
  // Create Jira ticket for an alert
  const createJiraTicket = useCallback(async (alert) => {
    try {
      if (useMockData) {
        // Mock ticket creation
        return {
          status: true,
          ticket_key: 'SETT-999',
          ticket_url: 'https://eventussecurity.atlassian.net/browse/SETT-999'
        };
      } else {
        // Use correct API endpoint
        const response = await api.post('/api/alerts/jira/create', alert);
        return response.data;
      }
    } catch (error) {
      console.error('Error creating Jira ticket:', error);
      return {
        status: false,
        message: error.response?.data?.message || 'Failed to create Jira ticket'
      };
    }
  }, [useMockData]);

  // Function to fetch user info from session ID
  const fetchUserInfo = useCallback(async (sessionId) => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'userInfo', value: true } });
    
    try {
      if (useMockData) {
        // Use mock data for user info
        setTimeout(() => {
          dispatch({ 
            type: ACTIONS.SET_USER_INFO, 
            payload: {
              username: 'mock_user',
              email: 'mock@example.com',
              roleId: 'admin',
              roleDescription: 'Administrator',
              tenantName: 'MockTenant',
              tenantCode: 'MT',
              isExternal: false,
              sessionId: sessionId || 'mock-session-id'
            } 
          });
          dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'userInfo', value: false } });
        }, 300);
      } else {
        // Get user info with caching (30 minutes TTL)
        const data = await getCachedOrFetch('/api/auth/user', { session_id: sessionId }, 30 * 60 * 1000);
        
        // Process the response data based on the format
        let userData;
        
        if (data && data.status && data.response_data) {
          // Parse the response_data string to JSON
          const parsedData = JSON.parse(data.response_data);
          
          if (parsedData.data) {
            if (parsedData.data.user_data.idp_data) {
              // IDP user format
              const idpData = parsedData.data.user_data.idp_data;
              const sessionResponse = parsedData.data.user_data.session_response;
              
              userData = {
                username: sessionResponse.username,
                email: sessionResponse.email || idpData.email,
                roleId: sessionResponse.roles?.role_id || '',
                roleDescription: sessionResponse.roles?.role_description || '',
                tenantName: sessionResponse.tenant?.tenant_name || '',
                tenantCode: sessionResponse.tenant?.tenant_code || '',
                isExternal: sessionResponse.is_external || false,
                sessionId: parsedData.data.session_id.replace(/"/g, '') // Remove quotes
              };
            } else {
              // Local user format
              const localUserData = parsedData.data.user_data;
              
              userData = {
                username: localUserData.username,
                email: localUserData.email,
                roleId: localUserData.roles?.role_id || '',
                roleDescription: localUserData.roles?.role_description || '',
                tenantName: localUserData.tenant?.tenant_name || '',
                tenantCode: localUserData.tenant?.tenant_code || '',
                isExternal: localUserData.is_external || false,
                sessionId: parsedData.data.session_id.replace(/"/g, '') // Remove quotes
              };
            }
          }
        }
        
        if (userData) {
          dispatch({ type: ACTIONS.SET_USER_INFO, payload: userData });
        } else {
          console.error('Failed to extract user data from response:', data);
        }
        
        dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'userInfo', value: false } });
        return userData;
      }
    } catch (error) {
      console.error('Error fetching user info:', error);
      dispatch({ type: ACTIONS.SET_ERROR, payload: 'Failed to fetch user information' });
      dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'userInfo', value: false } });
      return null;
    }
  }, [useMockData, getCachedOrFetch]);

  // Function to set current region
  const setCurrentRegion = useCallback((region) => {
    dispatch({ type: ACTIONS.SET_CURRENT_REGION, payload: region });
  }, []);

  // Function to fetch client costs
  const fetchClientCosts = useCallback(async (clientName, params = {}) => {
    if (!clientName) return null;
    
    dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'clientCosts', value: true } });
    
    try {
      if (useMockData) {
        // Use mock data
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // Generate mock client costs data
        const mockClientCosts = {
          client_name: clientName,
          total_cost: Math.floor(Math.random() * 10000) + 1000,
          cost_breakdown: {
            compute: Math.floor(Math.random() * 5000) + 500,
            storage: Math.floor(Math.random() * 3000) + 300,
            network: Math.floor(Math.random() * 2000) + 200
          }
        };
        
        dispatch({ 
          type: ACTIONS.SET_CLIENT_COSTS, 
          payload: { [clientName]: mockClientCosts } 
        });
        dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'clientCosts', value: false } });
        return mockClientCosts;
      } else {
        // Real API call
        const url = `/api/clients/${clientName}/costs`;
        const response = await api.get(url, { params });
        
        dispatch({ 
          type: ACTIONS.SET_CLIENT_COSTS, 
          payload: { [clientName]: response.data } 
        });
        dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'clientCosts', value: false } });
        return response.data;
      }
    } catch (error) {
      console.error(`Error fetching costs for client ${clientName}:`, error);
      dispatch({ type: ACTIONS.SET_ERROR, payload: `Failed to fetch costs for client ${clientName}` });
      dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'clientCosts', value: false } });
      return null;
    }
  }, [useMockData]);

  // Function to fetch all client licenses
  const fetchAllClientLicenses = useCallback(async () => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'clientLicenses', value: true } });
    
    try {
      if (useMockData) {
        // Use mock data
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // Generate mock client licenses data
        const mockClientLicenses = {
          licenses: Array.from({ length: 10 }, (_, i) => ({
            client_name: `Client${i+1}`,
            license_key: `LICENSE-${i+1}`,
            expiry_date: new Date(Date.now() + (Math.random() * 365 * 24 * 60 * 60 * 1000)).toISOString(),
            max_devices: Math.floor(Math.random() * 50) + 10
          }))
        };
        
        dispatch({ type: ACTIONS.SET_CLIENT_LICENSES, payload: mockClientLicenses.licenses });
        dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'clientLicenses', value: false } });
        return mockClientLicenses;
      } else {
        // Real API call - using getWithAuth to ensure user headers are sent
        console.log('Fetching all client licenses with authenticated headers');
        const response = await api.getWithAuth('/api/clients/licenses');
        
        // Add detailed logging for debugging server deployment issues
        console.log(`Loaded ${response.data.licenses?.length || 0} client licenses`);
        
        if (response.data.licenses && response.data.licenses.length > 0) {
          // Log sample license data
          console.log('Sample license data:', response.data.licenses.slice(0, 3));
          
          // Check for DICT child clients specifically
          const dictChildren = response.data.licenses.filter(license => 
            license.client_name !== 'DICT' && 
            license.client_name.toLowerCase().startsWith('dict')
          );
          console.log(`Found ${dictChildren.length} DICT child clients with licenses:`, 
            dictChildren.map(c => c.client_name));
          
          // Convert to object with client_name as key for easier lookup
          const licensesObj = {};
          response.data.licenses.forEach(license => {
            licensesObj[license.client_name] = license;
          });
          
          dispatch({ type: ACTIONS.SET_CLIENT_LICENSES, payload: licensesObj });
        } else {
          console.warn('No license data returned from API');
          dispatch({ type: ACTIONS.SET_CLIENT_LICENSES, payload: {} });
        }
        
        dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'clientLicenses', value: false } });
        return response.data;
      }
    } catch (error) {
      console.error('Error fetching client licenses:', error);
      dispatch({ type: ACTIONS.SET_ERROR, payload: 'Failed to fetch client licenses' });
      dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'clientLicenses', value: false } });
      return null;
    }
  }, [useMockData]);

  // Function to fetch a specific client license
  const fetchClientLicense = useCallback(async (clientName) => {
    if (!clientName) return null;
    
    dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'clientLicenses', value: true } });
    
    try {
      if (useMockData) {
        // Use mock data
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // Generate mock client license data
        const mockClientLicense = {
          client_name: clientName,
          license_key: `LICENSE-${clientName}`,
          expiry_date: new Date(Date.now() + (Math.random() * 365 * 24 * 60 * 60 * 1000)).toISOString(),
          max_devices: Math.floor(Math.random() * 50) + 10
        };
        
        dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'clientLicenses', value: false } });
        return mockClientLicense;
      } else {
        // Real API call - using getWithAuth to ensure user headers are sent
        //console.log(`Fetching license for client ${clientName} with authenticated headers`);
        const response = await api.getWithAuth(`/api/clients/${clientName}/license`);
        
        dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'clientLicenses', value: false } });
        return response.data;
      }
    } catch (error) {
      console.error(`Error fetching license for client ${clientName}:`, error);
      dispatch({ type: ACTIONS.SET_ERROR, payload: `Failed to fetch license for client ${clientName}` });
      dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'clientLicenses', value: false } });
      return null;
    }
  }, [useMockData]);

  // Function to save a client license
  const saveClientLicense = useCallback(async (clientName, licenseData) => {
    //console.log('====== UPDATED saveClientLicense FUNCTION CALLED ======');
    //console.log('Client name:', clientName);
    //console.log('License data:', licenseData);
    
    if (!clientName || !licenseData) return null;
    
    dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'clientLicenses', value: true } });
    
    try {
      if (useMockData) {
        // Use mock data
        await new Promise(resolve => setTimeout(resolve, 300));
        
        dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'clientLicenses', value: false } });
        return { success: true, message: 'License saved successfully' };
      } else {
        // Use direct fetch API with explicit headers
        //console.log('Saving license for client:', clientName);
        //console.log('License data:', licenseData);
        
        // Get user info from localStorage
        const userStr = localStorage.getItem('user');
        let username = 'unknown';
        let role = 'user';
        
        if (userStr) {
          try {
            const user = JSON.parse(userStr);
            username = user.username || 'unknown';
            role = user.role || 'user';
            //console.log('Using username and role from localStorage:', username, role);
          } catch (e) {
            console.warn('Failed to parse user data from localStorage', e);
          }
        }
        
        // Create headers with explicit user info
        const headers = {
          'Content-Type': 'application/json',
          'X-User-Name': username,
          'X-User-Role': role
        };
        
        //console.log('POST request headers:', headers);
        
        // Make the fetch request
        const response = await fetch(
          `${apiConfig.API_BASE_URL}/api/clients/${clientName}/license`,
          {
            method: 'POST',
            headers: headers,
            body: JSON.stringify(licenseData),
            mode: 'cors'
          }
        );
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('License save failed:', response.status, errorText);
          throw new Error(`Request failed with status ${response.status}: ${errorText}`);
        }
        
        const data = await response.json();
        //console.log('License save response:', data);
        
        // Refresh licenses after saving
        await fetchAllClientLicenses();
        
        dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'clientLicenses', value: false } });
        return data;
      }
    } catch (error) {
      console.error(`Error saving license for client ${clientName}:`, error);
      dispatch({ type: ACTIONS.SET_ERROR, payload: `Failed to save license for client ${clientName}` });
      dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'clientLicenses', value: false } });
      return { success: false, message: error.message || 'Failed to save license' };
    }
  }, [useMockData, fetchAllClientLicenses, dispatch, apiConfig.API_BASE_URL]);

  // Function to fetch total production costs
  const fetchTotalProductionCosts = useCallback(async (params = {}) => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'totalProductionCosts', value: true } });
    
    try {
      if (useMockData) {
        // Use mock data
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // Generate mock production costs data
        const mockProductionCosts = {
          rsyslog_size: Math.floor(Math.random() * 1000) + 100,
          production_cost: Math.floor(Math.random() * 50000) + 10000,
          cost_per_gb: Math.floor(Math.random() * 100) + 50,
          region: params.region || '',
          cost_details: {
            compute_cost: Math.floor(Math.random() * 30000) + 5000,
            boot_volume_cost: Math.floor(Math.random() * 10000) + 2000,
            block_volume_cost: Math.floor(Math.random() * 10000) + 3000
          }
        };
        
        dispatch({ type: ACTIONS.SET_TOTAL_PRODUCTION_COSTS, payload: mockProductionCosts });
        dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'totalProductionCosts', value: false } });
        return mockProductionCosts;
      } else {
        // Real API call - using getWithAuth to ensure user headers are sent
        //console.log('Fetching total production costs with authenticated headers');
        const response = await api.getWithAuth('/api/total_production_costs', { params });
        
        dispatch({ type: ACTIONS.SET_TOTAL_PRODUCTION_COSTS, payload: response.data });
        dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'totalProductionCosts', value: false } });
        return response.data;
      }
    } catch (error) {
      console.error('Error fetching total production costs:', error);
      dispatch({ type: ACTIONS.SET_ERROR, payload: 'Failed to fetch total production costs' });
      dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'totalProductionCosts', value: false } });
      return null;
    }
  }, [useMockData]);

  // Function to fetch client logs timeseries
  const fetchClientLogsTimeseries = useCallback(async (params = {}) => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'clientLogs', value: true } });
    
    try {
      if (useMockData) {
        // Use mock data
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // Generate mock timeseries data
        const dates = Array.from({ length: 30 }, (_, i) => {
          const date = new Date();
          date.setDate(date.getDate() - (29 - i));
          return date.toISOString().split('T')[0];
        });
        
        const mockTimeseries = {
          dates,
          series: [
            {
              name: 'Client1',
              data: dates.map(() => Math.floor(Math.random() * 100) + 10)
            },
            {
              name: 'Client2',
              data: dates.map(() => Math.floor(Math.random() * 80) + 5)
            },
            {
              name: 'Client3',
              data: dates.map(() => Math.floor(Math.random() * 60) + 20)
            }
          ]
        };
        
        dispatch({ type: ACTIONS.SET_CLIENT_LOGS_TIMESERIES, payload: mockTimeseries });
        dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'clientLogs', value: false } });
        return mockTimeseries;
      } else {
        // Real API call
        const response = await api.get('/api/clients/logs/timeseries', { params });
        
        dispatch({ type: ACTIONS.SET_CLIENT_LOGS_TIMESERIES, payload: response.data });
        dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'clientLogs', value: false } });
        return response.data;
      }
    } catch (error) {
      console.error('Error fetching client logs timeseries:', error);
      dispatch({ type: ACTIONS.SET_ERROR, payload: 'Failed to fetch client logs timeseries' });
      dispatch({ type: ACTIONS.SET_LOADING, payload: { key: 'clientLogs', value: false } });
      return null;
    }
  }, [useMockData]);

  // Function to set error message
  const setError = useCallback((message) => {
    dispatch({ type: ACTIONS.SET_ERROR, payload: message });
  }, []);

  // Function to clear error message
  const clearError = useCallback(() => {
    dispatch({ type: ACTIONS.SET_ERROR, payload: null });
  }, []);

  // Function to refresh data
  const refreshData = useCallback(async (region = '') => {
    // Refresh all data for the given region
    try {
      await Promise.all([
        fetchClients(region),
        fetchClientLogs({ region }),
        fetchTotalProductionCosts({ region })
      ]);
      return true;
    } catch (error) {
      console.error('Error refreshing data:', error);
      return false;
    }
  }, [fetchClients, fetchClientLogs, fetchTotalProductionCosts]);

  // Function to get full client name
  const getFullClientName = useCallback((clientName) => {
    // Import the actual function from utils if needed
    // For now, just return the client name
    return clientName;
  }, []);

  // Provide context value
  const contextValue = {
        ...state,
        fetchClients,
        fetchClientLogs,
        fetchClientDetails,
        fetchClientProducts,
        fetchProductDeviceIds,
        fetchStorageDetails,
        fetchStorageTrends,
        fetchStorageCosts,
        fetchCompartmentTree,
        fetchRegions,
        setCurrentRegion,
        fetchClientCosts,
        fetchAllClientLicenses,
        fetchClientLicense,
        saveClientLicense,
        fetchTotalProductionCosts,
        fetchClientLogsTimeseries,
        setError,
        clearError,
        refreshData,
        refreshAllData,
        forceRefreshAllData,
        getFullClientName,
        fetchAlerts,
        fetchJiraConfig,
        fetchAlertTickets,
        createJiraTicket,
        fetchHosts,
        fetchSummary,
        fetchSystemMetrics,
        fetchSystemMetricsForHosts,
        fetchClientCount,
        fetchHostClients,
        fetchUserInfo
  };

  return (
    <AppDataContext.Provider
      value={contextValue}
    >
      {children}
    </AppDataContext.Provider>
  );
};

// Custom hook to use the app data context
export const useAppData = () => {
  const context = useContext(AppDataContext);
  if (!context) {
    throw new Error('useAppData must be used within an AppDataProvider');
  }
  return context;
};

// Default export for the context
export default AppDataContext;